/* eslint-disable @typescript-eslint/ban-ts-comment */

import { useEffect } from 'react';

import useScript from 'hooks/useScript';
import { generateQueryString } from 'utils/helpers';

interface AppleUserResponse {
  email: string;
  name: {
    firstName: string;
    lastName: string;
  };
}

interface AppleResponse {
  authorization?: {
    code?: string;
    id_token?: string;
    state?: string;
  };
  user?: AppleUserResponse;
}

interface AppleLoginProps {
  autoLoad?: boolean;
  clientId: string;
  nonce?: string;
  onError: (error: unknown) => void;
  onSuccess: (response: AppleResponse) => void;
  redirectURI: string;
  responseMode?: string | 'query' | 'fragment' | 'form_post';
  responseType?: string | 'code' | 'id_token';
  scope?: string;
  state?: string;
  usePopup?: boolean;
}

export const useAppleLogin = ({
  clientId,
  redirectURI,
  state = '',
  responseMode = 'query',
  responseType = 'code',
  nonce,
  onSuccess,
  onError,
  scope = 'email name',
  autoLoad = false,
  usePopup = true
}: AppleLoginProps) => {
  const [loading] = useScript({
    src: 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  });

  const onClick = async (e?: MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (usePopup) {
      try {
        // @ts-ignore
        const data = await window.AppleID.auth.signIn();
        onSuccess(data);
      } catch (e) {
        onError(e);
      }
    } else {
      window.location.href = `https://appleid.apple.com/auth/authorize?${generateQueryString({
        client_id: clientId,
        nonce,
        redirect_uri: encodeURIComponent(redirectURI),
        response_mode: responseMode,
        response_type: responseType,
        scope: responseMode === 'query' ? '' : scope,
        state
      })}`;
    }
  };

  useEffect(() => {
    if (usePopup) return;
    if (autoLoad && !loading) {
      onClick();
    }

    if (responseMode === 'query' && responseType === 'code' && window && window.location) {
      let match;
      const pl = /\+/g; // Regex for replacing addition symbol with a space
      const search = /([^&=]+)=?([^&]*)/g;
      const decode = (s: string) => decodeURIComponent(s.replace(pl, ' '));
      const query = window.location.search.substring(1);
      const urlParams = {};

      while ((match = search.exec(query))) {
        // @ts-ignore
        urlParams[decode(match[1])] = decode(match[2]);
      }
      if ('code' in urlParams && typeof urlParams.code === 'string') {
        onSuccess({
          authorization: {
            code: urlParams.code
          }
        });
      }
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (usePopup && !loading) {
      // @ts-ignore
      window.AppleID.auth.init({
        clientId,
        nonce,
        redirectURI,
        scope,
        state,
        usePopup
      });

      if (autoLoad) {
        onClick();
      }
    }
    return () => {};
  }, [loading, usePopup]);

  return onClick;
};
